import { Reducer } from 'redux'
import {
  AgentChatMessage,
  AgentState,
  AgentActionTypes,
  AgentCode,
  AgentChatRole,
  AgentStatus
} from './types'
import {
  ComponentContext,
  ContextType,
  FileContext,
  getChatSessionCode
} from 'pages/app/components/agents/helpers'

export const initialState: AgentState = {}

const normalizeComponentContext = (value?: ComponentContext) => {
  if (!value) {
    return value
  }

  return {
    ...value,
    pinned: value.pinned ?? true
  }
}

const isPinnedComponent = (component: ComponentContext) => component.pinned ?? true

const replaceComponentContext = (
  prevContextList: ComponentContext[],
  value: ComponentContext,
  replaceUnpinned?: boolean,
  isSingle?: boolean
) => {
  if (isSingle) {
    return [value]
  }

  if (replaceUnpinned) {
    const pinnedComponents = prevContextList.filter(isPinnedComponent)
    const existsInPinned = pinnedComponents.some(
      (component) => component.component_id === value.component_id
    )

    return existsInPinned ? pinnedComponents : [...pinnedComponents, value]
  }

  const existingIndex = prevContextList.findIndex(
    (component) => component.component_id === value.component_id
  )

  if (existingIndex === -1) {
    return [...prevContextList, value]
  }

  const existingComponent = prevContextList[existingIndex]
  if (!isPinnedComponent(existingComponent) && isPinnedComponent(value)) {
    return prevContextList.map((component, index) => (index === existingIndex ? value : component))
  }

  return prevContextList
}

const getContextExists = (contextType: string, prevContextList: any[], value: any) => {
  if (contextType === ContextType.COMPONENT) {
    return prevContextList.some(
      (item: ComponentContext) => item?.component_id === value?.component_id
    )
  }

  if (contextType === ContextType.FILE) {
    return prevContextList.some((item: FileContext) => item?.path === value?.path)
  }

  return prevContextList.includes(value)
}

/** What a turn produced is shown once, never sent back with the next message. */
const withoutTurnResults = ({ generated_files, question, ...rest }: Record<string, any>) => rest

/** Patches this turn's answer, or adds an empty one before the turn's question. */
const attachToTurnAnswer = (
  messages: AgentChatMessage[],
  patch: Partial<AgentChatMessage>
): AgentChatMessage[] => {
  const updated = [...messages]
  const turnStart = updated.reduce(
    (start, message, index) => (message.role === AgentChatRole.user ? index + 1 : start),
    0
  )
  for (let index = updated.length - 1; index >= turnStart; index--) {
    if (updated[index].role === AgentChatRole.assistant) {
      updated[index] = { ...updated[index], ...patch }
      return updated
    }
  }
  const questionIndex = updated.findIndex(
    (message, index) => index >= turnStart && message.role === AgentChatRole.question
  )
  updated.splice(questionIndex === -1 ? updated.length : questionIndex, 0, {
    role: AgentChatRole.assistant,
    content: '',
    ...patch
  })
  return updated
}

const reducer: Reducer<AgentState> = (
  state: AgentState = initialState,
  action: { type: AgentActionTypes; payload?: any }
) => {
  switch (action.type) {
    case AgentActionTypes.SHOW: {
      return {
        ...state,
        show: action.payload.show
      }
    }
    case AgentActionTypes.UPDATE_AVAILABLE_AGENTS: {
      return {
        ...state,
        availableAgents: action.payload
      }
    }

    case AgentActionTypes.UPDATE_SELECTED_AGENT: {
      return {
        ...state,
        selectedAgent: action.payload
      }
    }

    case AgentActionTypes.UPDATE_CHAT_SESSIONS: {
      return {
        ...state,
        sessions: action.payload
      }
    }
    case AgentActionTypes.ATTACH_SAVED_MEMORY: {
      const { sessionCode, memory } = action.payload
      const session = state.sessions?.[sessionCode]
      if (!session || !memory) {
        return state
      }

      // The status is left alone on purpose: this arrives after COMPLETED and
      // must not reopen the turn.
      const messages = attachToTurnAnswer(session.messages || [], { savedMemory: memory })

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [sessionCode]: { ...session, messages }
        }
      }
    }

    case AgentActionTypes.UPDATE_SESSION_MESSAGE: {
      const {
        sessionCode,
        message,
        status,
        metadata,
        reasoningText,
        toolName,
        traceId
      } = action.payload

      if (!state.sessions || !state.sessions[sessionCode]) {
        return state
      }

      const session = state.sessions[sessionCode]
      const prevStatus = session.status
      const newStatus = (status || prevStatus) as AgentStatus
      const TOOL_USE_CYCLE = [AgentStatus.USING_TOOL, AgentStatus.EXECUTING_TOOL]
      const TOOL_NAME_CYCLE = [...TOOL_USE_CYCLE, AgentStatus.EXECUTING_CONTEXT_NODES]

      const finishingReasoning =
        prevStatus === AgentStatus.REASONING &&
        newStatus !== AgentStatus.REASONING &&
        !!session.reasoningText

      const finishingToolUse =
        TOOL_USE_CYCLE.includes(prevStatus as AgentStatus) &&
        !TOOL_USE_CYCLE.includes(newStatus) &&
        !!session.toolName

      let updatedMessages = session.messages || []

      if (traceId) {
        updatedMessages = [...updatedMessages]
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === AgentChatRole.user && !updatedMessages[i].traceId) {
            updatedMessages[i] = { ...updatedMessages[i], traceId }
            break
          }
        }
      }

      if (message || finishingReasoning || finishingToolUse) {
        if (updatedMessages === session.messages) {
          updatedMessages = [...updatedMessages]
        }

        if (finishingReasoning) {
          updatedMessages.push({ role: AgentChatRole.reasoning, content: session.reasoningText })
        }

        if (finishingToolUse) {
          updatedMessages.push({ role: AgentChatRole.tool_call, content: session.toolName })
        }

        // The update that ends a reasoning or tool step can carry the first answer
        // chunk, which must land after that step.
        if (message) {
          const lastMessage = updatedMessages[updatedMessages.length - 1]
          if (updatedMessages.length === 0 || lastMessage.role !== AgentChatRole.assistant) {
            updatedMessages.push({ role: AgentChatRole.assistant, content: message })
          } else {
            updatedMessages[updatedMessages.length - 1] = {
              ...lastMessage,
              content: (lastMessage.content || '') + message
            }
          }
        }
      }

      const contextUsage = (metadata as Record<string, any> | undefined)?.context_usage

      // Files generated during the turn are reported once, with COMPLETED, so
      // they land under the finished answer instead of inside the activity
      // block the tool calls built.
      const generatedFiles = (metadata as Record<string, any> | undefined)?.generated_files
      if (generatedFiles?.length) {
        updatedMessages = attachToTurnAnswer(updatedMessages, { generatedFiles })
      }

      const question = (metadata as Record<string, any> | undefined)?.question
      if (status === AgentStatus.COMPLETED && question) {
        if (updatedMessages === session.messages) {
          updatedMessages = [...updatedMessages]
        }
        updatedMessages.push({ role: AgentChatRole.question, question })
      }

      const updatedSession = {
        ...session,
        ...(updatedMessages !== session.messages && { messages: updatedMessages }),
        ...(status && { status }),
        ...(metadata !== undefined && { metadata: metadata && withoutTurnResults(metadata) }),
        ...(contextUsage && { contextUsage }),
        ...(status === AgentStatus.COMPLETED && metadata !== null && { isStopping: false }),
        ...(reasoningText && { reasoningText: (session.reasoningText || '') + reasoningText }),
        ...(status && status !== AgentStatus.REASONING && { reasoningText: '' }),
        ...(toolName && { toolName }),
        ...(status && !TOOL_NAME_CYCLE.includes(status as AgentStatus) && { toolName: '' })
      }

      if (
        updatedSession.messages === session.messages &&
        updatedSession.status === session.status &&
        updatedSession.metadata === session.metadata &&
        updatedSession.contextUsage === session.contextUsage &&
        updatedSession.isStopping === session.isStopping &&
        updatedSession.reasoningText === session.reasoningText &&
        updatedSession.toolName === session.toolName
      ) {
        return state
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [sessionCode]: updatedSession
        }
      }
    }
    case AgentActionTypes.RESET_AGENT_DATA: {
      return {
        ...initialState
      }
    }
    case AgentActionTypes.START_COMPONENT_CONTEXT_PICKER: {
      return {
        ...state,
        componentContextPicker: action.payload
      }
    }
    case AgentActionTypes.STOP_COMPONENT_CONTEXT_PICKER: {
      return {
        ...state,
        componentContextPicker: undefined
      }
    }
    case AgentActionTypes.SET_CURRENT_SESSION_CODE: {
      return {
        ...state,
        currentSessionCode: action.payload
      }
    }
    case AgentActionTypes.ADD_CONTEXT: {
      const { agentCode, contextType, value, options } = action.payload
      const { isSingle, replaceUnpinned } = options || {}
      const prevChatSessionCode =
        state.currentSessionCode || getChatSessionCode(agentCode as AgentCode)
      const prevContextList = state.sessions?.[prevChatSessionCode]?.context?.[contextType] || []
      const normalizedValue =
        contextType === ContextType.COMPONENT ? normalizeComponentContext(value) : value

      if (!normalizedValue) {
        return state
      }

      const exists = getContextExists(contextType, prevContextList, normalizedValue)
      const newContextList =
        contextType === ContextType.COMPONENT
          ? replaceComponentContext(
              prevContextList as ComponentContext[],
              normalizedValue as ComponentContext,
              replaceUnpinned,
              isSingle
            )
          : isSingle
          ? [normalizedValue]
          : exists
          ? prevContextList
          : [...prevContextList, normalizedValue]

      // Calculate new context with the updated list
      const newContext = {
        ...(state.sessions?.[prevChatSessionCode]?.context || {}),
        [contextType]: newContextList
      }

      const chatSessionCode = getChatSessionCode(agentCode, newContext)

      let chatSessionId = state.sessions?.[chatSessionCode]?.chatSessionId
      if (!chatSessionId) {
        chatSessionId = `session_${new Date().getTime()}`
      }

      // Don't copy files from previous component session to new component session
      let contextToCopy
      if (chatSessionCode !== prevChatSessionCode) {
        contextToCopy = state.sessions?.[prevChatSessionCode]?.context || {}
      } else {
        contextToCopy = state.sessions?.[chatSessionCode]?.context || {}
      }

      return {
        ...state,
        currentSessionCode: chatSessionCode,
        sessions: {
          ...state.sessions,
          [chatSessionCode]: {
            ...state.sessions?.[chatSessionCode],
            chatSessionId,
            chatSessionCode,
            context: {
              ...contextToCopy,
              [contextType]: newContextList
            }
          }
        }
      }
    }
    case AgentActionTypes.REMOVE_CONTEXT: {
      const { agentCode, contextType, value } = action.payload
      const prevChatSessionCode =
        state.currentSessionCode || getChatSessionCode(agentCode as AgentCode)
      const prevContextList = state.sessions?.[prevChatSessionCode].context?.[contextType] || []
      const newContextList =
        contextType === ContextType.COMPONENT
          ? prevContextList.filter((item: any) => item.component_id !== value)
          : contextType === ContextType.FILE
          ? prevContextList.filter((item: any) => item.path !== value)
          : prevContextList.filter((item: string) => item !== value)

      // Calculate new context with the updated list
      const newContext = {
        ...(state.sessions?.[prevChatSessionCode]?.context || {}),
        [contextType]: newContextList
      }

      const chatSessionCode = getChatSessionCode(agentCode, newContext)

      let contextToCopy
      if (chatSessionCode !== prevChatSessionCode) {
        contextToCopy = state.sessions?.[prevChatSessionCode]?.context || {}
      } else {
        contextToCopy = state.sessions?.[chatSessionCode]?.context || {}
      }

      return {
        ...state,
        currentSessionCode: chatSessionCode,
        sessions: {
          ...state.sessions,
          [chatSessionCode]: {
            ...state.sessions?.[chatSessionCode],
            chatSessionCode,
            context: {
              ...contextToCopy,
              [contextType]: newContextList
            }
          }
        }
      }
    }

    case AgentActionTypes.CLEAR_CONTEXT: {
      const { agentCode, contextTypes, options } = action.payload
      const { onlyUnpinned } = options || {}
      const prevChatSessionCode =
        state.currentSessionCode || getChatSessionCode(agentCode as AgentCode)
      const prevContext = state.sessions?.[prevChatSessionCode]?.context || {}
      const targetContextTypes =
        contextTypes && contextTypes.length > 0 ? contextTypes : Object.values(ContextType)
      const clearedContextEntries: Record<string, any> = {}

      targetContextTypes.forEach((contextType: ContextType) => {
        const prevContextList = prevContext?.[contextType] || []
        clearedContextEntries[contextType] =
          contextType === ContextType.COMPONENT && onlyUnpinned
            ? (prevContextList as ComponentContext[]).filter(isPinnedComponent)
            : []
      })

      const newContext = {
        ...prevContext,
        ...clearedContextEntries
      }
      const chatSessionCode = getChatSessionCode(agentCode as AgentCode, newContext)

      let contextToCopy
      if (chatSessionCode !== prevChatSessionCode) {
        contextToCopy = state.sessions?.[prevChatSessionCode]?.context || {}
      } else {
        contextToCopy = state.sessions?.[chatSessionCode]?.context || {}
      }

      return {
        ...state,
        currentSessionCode: chatSessionCode,
        sessions: {
          ...state.sessions,
          [chatSessionCode]: {
            ...state.sessions?.[chatSessionCode],
            chatSessionCode,
            context: {
              ...contextToCopy,
              ...clearedContextEntries
            }
          }
        }
      }
    }

    case AgentActionTypes.SET_STOPPING_STATE: {
      const { agentCode, isStopping } = action.payload
      const currentChatSessionCode =
        state.currentSessionCode || getChatSessionCode(agentCode as AgentCode)

      if (!state.sessions || !state.sessions[currentChatSessionCode]) {
        return state
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [currentChatSessionCode]: {
            ...state.sessions[currentChatSessionCode],
            isStopping
          }
        }
      }
    }

    case AgentActionTypes.SET_SESSION_HISTORY: {
      const { sessions, loading } = action.payload
      return {
        ...state,
        sessionHistory: sessions,
        sessionHistoryLoading: loading ?? false
      }
    }

    case AgentActionTypes.LOAD_SESSION_HISTORY: {
      return {
        ...state,
        sessionHistoryLoading: true
      }
    }

    default: {
      return state
    }
  }
}

// Instead of using default export, we use named exports. That way we can group these exports
// inside the `index.js` folder.
export { reducer as agentReducer }
